import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { User, Lock, Globe, Bell, Shield, RefreshCw, Check, AlertCircle, Eye, EyeOff, Clock, Server, RotateCw, Database, Activity, Sliders, Monitor, Upload, Image, AlignLeft, ToggleLeft, ToggleRight, Layers, Film, Wrench, Users, UserPlus, UserX, KeyRound, Trash2, Edit3, Crown, UserCheck, ShieldCheck, ShieldOff, ScanLine } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

function Settings({ user, onLogout, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState('profile');
  const { language, setLanguage, timezone, setTimezone, t, getAllTimezones } = useLanguage();
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [restartConfirm, setRestartConfirm] = useState(null); // 'nginx' | 'backend' | 'database' | null
  const [restartLoading, setRestartLoading] = useState(null); // which service is restarting
  const [sslStatus, setSslStatus] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled]       = useState(false);
  const [twoFAStep, setTwoFAStep]             = useState('idle'); // 'idle' | 'qr' | 'confirm' | 'disable'
  const [twoFASetupData, setTwoFASetupData]   = useState(null);  // {qr_data_uri, manual_key}
  const [twoFACode, setTwoFACode]             = useState('');
  const [twoFADisableForm, setTwoFADisableForm] = useState({ password: '', code: '' });
  const [twoFALoading, setTwoFALoading]       = useState(false);

  // Transcoding settings state
  const [transcodingConfig, setTranscodingConfig] = useState({
    vodProfiles: ['360p', '480p', '720p'],
    vodPreset: 'medium',
    liveABREnabled: false,
    liveProfiles: ['360p', '480p', '720p'],
    livePreset: 'veryfast',
    liveMaxBitrate: 2500
  });
  const [transcodingLoading, setTranscodingLoading] = useState(false);

  // Player branding state
  const [branding, setBranding] = useState({
    watermark:   { enabled: false, url: '', position: 'bottom-right', opacity: 0.8, size: 100 },
    ticker:      { enabled: false, text: '', fontSize: 14, fontWeight: 500 },
    transitions: { enabled: true, duration: 2 },
    sloop:       { enabled: false, url: '', videoName: '' }
  });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const watermarkFileRef = useRef(null);
  const sloopFileRef = useRef(null);

  // Maintenance mode state
  const [maintenance, setMaintenance] = useState({ enabled: false, message: 'Estamos realizando tareas de mantenimiento. Volvemos pronto.' });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Alerts config state
  const [alertsConfig, setAlertsConfig] = useState({
    email: { enabled: false, smtp_host: '', smtp_port: 587, smtp_secure: false, smtp_user: '', smtp_pass: '', from: '', to: '' },
    webhook: { enabled: false, url: '', secret: '' },
    events: { stream_offline: true, stream_online: false, disk_warning: true, disk_critical: true, ffmpeg_crash: true },
    disk_threshold_warning: 80,
    disk_threshold_critical: 90,
    disk_path: '/var/www/hls'
  });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);

  // Users management state
  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showResetPassModal, setShowResetPassModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUserForm, setNewUserForm] = useState({ email: '', full_name: '', password: '', role: 'operator' });
  const [resetPassForm, setResetPassForm] = useState({ new_password: '', confirm: '' });
  const [showNewUserPass, setShowNewUserPass] = useState(false);
  const [showResetPassField, setShowResetPassField] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/api/auth/profile', {
        full_name: formData.full_name,
        email: formData.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (onUserUpdate) {
        onUserUpdate(response.data.user);
      }
      
      toast.success('Perfil actualizado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLanguage = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settings', { language, timezone }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferencias guardadas exitosamente');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.new_password.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/auth/change-password', {
        current_password: formData.current_password,
        new_password: formData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setFormData({ 
        ...formData, 
        current_password: '', 
        new_password: '', 
        confirm_password: '' 
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar contraseña');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ssl') {
      handleCheckSSL();
    }
    if (activeTab === 'transcoding') {
      loadTranscodingSettings();
    }
    if (activeTab === 'branding') {
      loadBranding();
    }
    if (activeTab === 'maintenance') {
      loadMaintenance();
    }
    if (activeTab === 'notifications') {
      loadAlertsConfig();
    }
    if (activeTab === 'users') {
      fetchUsers();
    }
    if (activeTab === 'security') {
      load2FAStatus();
    }
  }, [activeTab]);

  // ── 2FA helpers ─────────────────────────────────────────────────────────────

  const load2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/auth/2fa/status', { headers: { Authorization: `Bearer ${token}` } });
      setTwoFAEnabled(res.data.enabled);
      setTwoFAStep('idle');
    } catch {}
  };

  const handle2FASetup = async () => {
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/auth/2fa/setup', {}, { headers: { Authorization: `Bearer ${token}` } });
      setTwoFASetupData(res.data);
      setTwoFACode('');
      setTwoFAStep('qr');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al iniciar configuración 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handle2FAEnable = async () => {
    if (twoFACode.length !== 6) { toast.error('El código debe tener 6 dígitos'); return; }
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/auth/2fa/enable', { totp_code: twoFACode }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('2FA activado exitosamente');
      setTwoFAEnabled(true);
      setTwoFAStep('idle');
      setTwoFASetupData(null);
      setTwoFACode('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Código incorrecto');
      setTwoFACode('');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handle2FADisable = async () => {
    if (!twoFADisableForm.password || twoFADisableForm.code.length !== 6) {
      toast.error('Contraseña y código de 6 dígitos son requeridos');
      return;
    }
    setTwoFALoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/auth/2fa/disable', {
        headers: { Authorization: `Bearer ${token}` },
        data: { current_password: twoFADisableForm.password, totp_code: twoFADisableForm.code }
      });
      toast.success('2FA desactivado exitosamente');
      setTwoFAEnabled(false);
      setTwoFAStep('idle');
      setTwoFADisableForm({ password: '', code: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al desactivar 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const loadTranscodingSettings = async () => {
    setTranscodingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/transcoding', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTranscodingConfig(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración de transcodificación');
    } finally {
      setTranscodingLoading(false);
    }
  };

  const handleSaveTranscoding = async () => {
    setTranscodingLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settings/transcoding', transcodingConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración de transcodificación guardada');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar configuración');
    } finally {
      setTranscodingLoading(false);
    }
  };

  const toggleProfile = (profileKey, profileName) => {
    setTranscodingConfig(prev => {
      const arr = prev[profileKey] || [];
      const updated = arr.includes(profileName)
        ? arr.filter(p => p !== profileName)
        : [...arr, profileName];
      return { ...prev, [profileKey]: updated };
    });
  };

  // ── Branding handlers ──────────────────────────────────────────────────────
  const loadBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/branding', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setBranding(prev => ({
          watermark:   { ...prev.watermark,   ...response.data.watermark   },
          ticker:      { ...prev.ticker,      ...response.data.ticker      },
          transitions: { ...prev.transitions, ...response.data.transitions },
          sloop:       { ...prev.sloop,       ...response.data.sloop       }
        }));
      }
    } catch (_) {}
  };

  const handleSaveBranding = async () => {
    setBrandingLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settings/branding', branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración del reproductor guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleWatermarkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setBrandingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/settings/upload-watermark', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({ ...prev, watermark: { ...prev.watermark, url: res.data.url } }));
      toast.success('Logo subido correctamente');
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setBrandingLoading(false);
      if (watermarkFileRef.current) watermarkFileRef.current.value = '';
    }
  };
  const handleSloopUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('video', file);
    setBrandingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/settings/upload-sloop', fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({
        ...prev,
        sloop: { ...prev.sloop, url: res.data.url, videoName: file.name }
      }));
      toast.success('Video de emergencia subido correctamente');
    } catch (error) {
      toast.error('Error al subir el video de emergencia');
    } finally {
      setBrandingLoading(false);
      if (sloopFileRef.current) sloopFileRef.current.value = '';
    }
  };
  // ── End branding handlers ──────────────────────────────────────────────────

  const loadMaintenance = async () => {
    try {
      const { data } = await axios.get('/api/settings/maintenance');
      setMaintenance({
        enabled: Boolean(data.enabled),
        message: data.message || 'Estamos realizando tareas de mantenimiento. Volvemos pronto.'
      });
    } catch {
      // use default
    }
  };

  const saveMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settings/maintenance', maintenance, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(maintenance.enabled ? 'Modo mantenimiento activado' : 'Modo mantenimiento desactivado');
    } catch {
      toast.error('Error al guardar configuración de mantenimiento');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const loadAlertsConfig = async () => {
    setAlertsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/settings/alerts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertsConfig(prev => ({ ...prev, ...data }));
    } catch {
      // use defaults
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleSaveAlerts = async () => {
    setAlertsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/settings/alerts', alertsConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración de alertas guardada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar configuración de alertas');
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/settings/alerts/test-email', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email de prueba enviado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email de prueba');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/settings/alerts/test-webhook', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Webhook de prueba enviado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar webhook de prueba');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleCheckSSL = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/ssl-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSslStatus(response.data);
    } catch (error) {
      toast.error('Error al verificar SSL');
    } finally {
      setLoading(false);
    }
  };

  const handleRestartService = async (service) => {
    // First click → show inline confirmation
    if (restartConfirm !== service) {
      setRestartConfirm(service);
      return;
    }
    // Second click (confirmed) → execute
    setRestartConfirm(null);
    setRestartLoading(service);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/settings/restart/${service}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || `${service} reiniciado exitosamente`);
    } catch (error) {
      toast.error(error.response?.data?.error || `Error al reiniciar ${service}`);
    } finally {
      setRestartLoading(null);
    }
  };

  const handleCheckSystemStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings/system-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado del sistema verificado');
      return response.data;
    } catch (error) {
      toast.error('Error al verificar estado del sistema');
    } finally {
      setLoading(false);
    }
  };

  // ── Users management functions ──────────────────────────────────────
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      setUsersData(res.data.users);
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/users', newUserForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuario creado exitosamente');
      setShowCreateUserModal(false);
      setNewUserForm({ email: '', full_name: '', password: '', role: 'operator' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear usuario');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/users/${editingUser.id}`, {
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuario actualizado');
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar usuario');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/users/${userId}/status`, { is_active: !currentStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Usuario ${!currentStatus ? 'activado' : 'desactivado'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuario eliminado');
      setDeletingUserId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassForm.new_password !== resetPassForm.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/users/${editingUser.id}/reset-password`, { new_password: resetPassForm.new_password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contraseña restablecida');
      setShowResetPassModal(false);
      setResetPassForm({ new_password: '', confirm: '' });
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al restablecer contraseña');
    } finally {
      setUsersLoading(false);
    }
  };

  const ROLE_META = {
    admin:    { label: 'Admin',    color: 'bg-red-500/20 text-red-400 border border-red-500/30',       icon: Crown },
    operator: { label: 'Operador', color: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',    icon: UserCheck },
    viewer:   { label: 'Visor',    color: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',    icon: Eye },
    user:     { label: 'Usuario',  color: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',    icon: User },
  };

  const tabs = [
    { id: 'profile',       label: t('profile'),          icon: User,      color: 'from-violet-500 to-purple-600',    glow: 'shadow-violet-500/30' },
    { id: 'security',      label: t('security'),          icon: Shield,    color: 'from-red-500 to-rose-600',         glow: 'shadow-red-500/30' },
    { id: 'ssl',           label: t('ssl'),               icon: Lock,      color: 'from-amber-500 to-orange-500',     glow: 'shadow-amber-500/30' },
    { id: 'notifications', label: t('notifications'),     icon: Bell,      color: 'from-indigo-500 to-blue-500',      glow: 'shadow-indigo-500/30' },
    { id: 'language',      label: t('language'),          icon: Globe,     color: 'from-teal-500 to-cyan-500',        glow: 'shadow-teal-500/30' },
    { id: 'transcoding',   label: 'Transcodificación',    icon: Sliders,   color: 'from-fuchsia-500 to-pink-500',     glow: 'shadow-fuchsia-500/30' },
    { id: 'branding',      label: 'Reproductor',          icon: Monitor,   color: 'from-sky-500 to-blue-400',         glow: 'shadow-sky-500/30' },
    { id: 'maintenance',   label: 'Mantenimiento',        icon: Wrench,    color: 'from-orange-500 to-yellow-500',    glow: 'shadow-orange-500/30' },
    { id: 'system',        label: 'Sistema',              icon: Server,    color: 'from-emerald-500 to-green-400',    glow: 'shadow-emerald-500/30' },
    ...(user?.role === 'admin' ? [
      { id: 'users', label: 'Usuarios', icon: Users, color: 'from-cyan-500 to-blue-500', glow: 'shadow-cyan-500/30' }
    ] : [])
  ];

  const activeTabMeta = tabs.find(t => t.id === activeTab);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-[10px] min-h-screen max-w-[1600px] mx-auto">

        {/* ── Premium Page Header ────────────────────────────────────────── */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-gray-900 dark:text-white">
            Configuración
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-base flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-accent-purple rounded-full"></span>
            Administra tu cuenta y preferencias del sistema
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* ── Sidebar: vertical on desktop, horizontal scroll on mobile ─── */}
          <div className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-6">

            {/* Mobile: horizontal scroll icon tabs */}
            <div className="lg:hidden glass-card mb-4 overflow-hidden">
              <div className="flex overflow-x-auto gap-1 p-2" style={{ scrollbarWidth: 'none' }}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all duration-200 min-w-[60px] ${
                        isActive ? 'bg-white/10 text-white' : 'text-gray-400'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive ? `bg-gradient-to-br ${tab.color} shadow-md ${tab.glow}` : 'bg-white/5'
                      }`}>
                        <Icon size={15} className="text-white" />
                      </div>
                      <span className="text-[10px] font-medium leading-tight text-center">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop: vertical sidebar */}
            <div className="hidden lg:block glass-card overflow-hidden">
              {/* User identity card */}
              <div className="px-4 py-4 border-b border-white/10 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center
                                text-white font-bold text-sm
                                bg-gradient-to-br from-accent-purple via-accent-indigo to-accent-emerald
                                shadow-lg shadow-accent-purple/30">
                  {user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) :
                   user?.email ? user.email.split('@')[0].slice(0,2).toUpperCase() : 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {user?.full_name || user?.email?.split('@')[0]}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{user?.email}</div>
                </div>
              </div>

              <nav className="p-2 space-y-0.5">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-3 group relative ${
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-gradient-to-b from-accent-purple to-accent-indigo rounded-r-full" />
                      )}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                        isActive
                          ? `bg-gradient-to-br ${tab.color} shadow-md ${tab.glow}`
                          : 'bg-white/5 group-hover:bg-white/10'
                      }`}>
                        <Icon size={15} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'} />
                      </div>
                      <span className="text-sm font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* ── Content ───────────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* Section header banner */}
            {activeTabMeta && (
              <div className="glass-card mb-4 px-5 py-4 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0
                                bg-gradient-to-br ${activeTabMeta.color} shadow-lg ${activeTabMeta.glow}`}>
                  {React.createElement(activeTabMeta.icon, { size: 20, className: 'text-white' })}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white leading-tight">{activeTabMeta.label}</h2>
                  <p className="text-xs text-gray-400">
                    {activeTab === 'profile'       && 'Actualiza tu información personal'}
                    {activeTab === 'security'      && 'Gestiona tu contraseña y acceso'}
                    {activeTab === 'ssl'           && 'Certificados SSL y seguridad HTTPS'}
                    {activeTab === 'notifications' && 'Alertas por email y webhooks'}
                    {activeTab === 'language'      && 'Idioma, zona horaria y localización'}
                    {activeTab === 'transcoding'   && 'Perfiles de calidad y codificación'}
                    {activeTab === 'branding'      && 'Watermark, ticker y aspecto del player'}
                    {activeTab === 'maintenance'   && 'Modo mantenimiento para el player público'}
                    {activeTab === 'system'        && 'Reinicio de servicios del sistema'}
                    {activeTab === 'users'         && 'Gestiona los usuarios y sus permisos de acceso'}
                  </p>
                </div>
              </div>
            )}

            <div className="glass-card p-6 sm:p-8">
              {activeTab === 'profile' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      {t('profile')}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Actualiza tu información personal
                    </p>
                  </div>

                  <form onSubmit={handleUpdateProfile} className="space-y-6">
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <User size={18} className="text-accent-purple" />
                        {t('fullName')}
                      </label>
                      <input
                        type="text"
                        className="input-glass"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        placeholder="Tu nombre completo"
                      />
                    </div>

                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Globe size={18} className="text-accent-indigo" />
                        {t('email')}
                      </label>
                      <input
                        type="email"
                        className="input-glass"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        className="btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="loading-premium w-4 h-4"></div>
                            Guardando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Check size={18} />
                            {t('updateProfile')}
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      {t('security')}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Actualiza tu contraseña de acceso
                    </p>
                  </div>

                  <form onSubmit={handleChangePassword} className="space-y-6">
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Lock size={18} className="text-accent-purple" />
                        {t('currentPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.current ? "text" : "password"}
                          className="input-glass pr-12"
                          value={formData.current_password}
                          onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                          placeholder="Tu contraseña actual"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                          {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Shield size={18} className="text-accent-indigo" />
                        {t('newPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.new ? "text" : "password"}
                          className="input-glass pr-12"
                          value={formData.new_password}
                          onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                          placeholder="Nueva contraseña (mínimo 8 caracteres)"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                          {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {formData.new_password && formData.new_password.length < 8 && (
                        <p className="mt-2 text-xs text-yellow-400 flex items-center gap-1">
                          <AlertCircle size={14} />
                          La contraseña debe tener al menos 8 caracteres
                        </p>
                      )}
                    </div>

                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Check size={18} className="text-accent-emerald" />
                        {t('confirmPassword')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.confirm ? "text" : "password"}
                          className="input-glass pr-12"
                          value={formData.confirm_password}
                          onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                          placeholder="Confirmar nueva contraseña"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                          {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                        <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle size={14} />
                          Las contraseñas no coinciden
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        className="btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="loading-premium w-4 h-4"></div>
                            Actualizando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Shield size={18} />
                            {t('updatePassword')}
                          </span>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* ── 2FA Section ── */}
                  <div className="mt-10">
                    <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                      <ShieldCheck size={20} className="text-accent-indigo" />
                      Autenticación de dos factores (2FA)
                    </h3>
                    <p className="text-gray-400 text-sm mb-6">
                      Añade una segunda capa de seguridad a tu cuenta. Al activar 2FA, necesitarás un código de tu app autenticadora cada vez que inicies sesión.
                    </p>

                    {/* Status badge */}
                    <div className="glass-card p-5 border border-white/5 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {twoFAEnabled ? (
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <ShieldCheck size={20} color="white" />
                            </div>
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #374151' }}>
                              <ShieldOff size={20} color="#6b7280" />
                            </div>
                          )}
                          <div>
                            <p className="text-white font-semibold text-sm">
                              {twoFAEnabled ? '2FA Activado' : '2FA Desactivado'}
                            </p>
                            <p className="text-gray-400 text-xs mt-0.5">
                              {twoFAEnabled
                                ? 'Tu cuenta está protegida con un segundo factor'
                                : 'Tu cuenta solo requiere contraseña para acceder'}
                            </p>
                          </div>
                        </div>
                        {twoFAStep === 'idle' && (
                          twoFAEnabled ? (
                            <button
                              onClick={() => setTwoFAStep('disable')}
                              className="text-sm px-4 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                              Desactivar
                            </button>
                          ) : (
                            <button
                              onClick={handle2FASetup}
                              disabled={twoFALoading}
                              className="text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                              {twoFALoading ? 'Generando...' : 'Activar 2FA'}
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* QR step */}
                    {twoFAStep === 'qr' && twoFASetupData && (
                      <div className="glass-card p-6 border border-indigo-500/20 space-y-5">
                        <p className="text-white font-semibold flex items-center gap-2">
                          <ScanLine size={18} className="text-accent-indigo" />
                          Escanea el código QR con tu app autenticadora
                        </p>
                        <p className="text-gray-400 text-sm">
                          Usa Google Authenticator, Authy, Bitwarden o cualquier app compatible con TOTP.
                        </p>

                        {/* QR Image */}
                        {twoFASetupData.qr_data_uri ? (
                          <div className="flex justify-center">
                            <div style={{ background: 'white', padding: 12, borderRadius: 12, display: 'inline-block' }}>
                              <img src={twoFASetupData.qr_data_uri} alt="QR 2FA" style={{ width: 200, height: 200, display: 'block' }} />
                            </div>
                          </div>
                        ) : (
                          <div className="text-yellow-400 text-sm flex items-center gap-2">
                            <AlertCircle size={16} />
                            QR no disponible — usa el código manual
                          </div>
                        )}

                        {/* Manual code */}
                        <div>
                          <p className="text-gray-400 text-xs mb-2">¿No puedes escanear? Ingresa este código manualmente:</p>
                          <div style={{ background: '#1a1e2e', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: 15, letterSpacing: '0.15em', color: '#a5b4fc', wordBreak: 'break-all' }}>
                            {twoFASetupData.manual_key}
                          </div>
                        </div>

                        {/* Confirm code */}
                        <div>
                          <label className="block text-gray-300 text-sm font-semibold mb-2">
                            Ingresa el código de 6 dígitos para confirmar:
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={twoFACode}
                            onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            style={{ width: '100%', padding: '12px 16px', background: '#1a1e2e', border: '1px solid #374151', borderRadius: 10, color: '#f3f4f6', fontSize: 20, letterSpacing: '0.3em', textAlign: 'center', outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={handle2FAEnable}
                            disabled={twoFALoading || twoFACode.length !== 6}
                            className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {twoFALoading ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Verificando...</> : <><ShieldCheck size={16} />Confirmar y Activar</>}
                          </button>
                          <button
                            onClick={() => { setTwoFAStep('idle'); setTwoFASetupData(null); setTwoFACode(''); }}
                            className="px-4 py-2.5 rounded-lg border border-white/10 text-gray-400 text-sm hover:border-white/20 hover:text-white transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Disable step */}
                    {twoFAStep === 'disable' && (
                      <div className="glass-card p-6 border border-red-500/20 space-y-4">
                        <p className="text-white font-semibold flex items-center gap-2">
                          <ShieldOff size={18} className="text-red-400" />
                          Desactivar autenticación de dos factores
                        </p>
                        <p className="text-gray-400 text-sm">
                          Para confirmar, introduce tu contraseña actual y el código de tu app autenticadora.
                        </p>

                        <div>
                          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">Contraseña actual</label>
                          <input
                            type="password"
                            value={twoFADisableForm.password}
                            onChange={(e) => setTwoFADisableForm({ ...twoFADisableForm, password: e.target.value })}
                            placeholder="Tu contraseña"
                            style={{ width: '100%', padding: '12px 14px', background: '#1a1e2e', border: '1px solid #374151', borderRadius: 10, color: '#f3f4f6', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        <div>
                          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">Código 2FA</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={twoFADisableForm.code}
                            onChange={(e) => setTwoFADisableForm({ ...twoFADisableForm, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder="000000"
                            style={{ width: '100%', padding: '12px 14px', background: '#1a1e2e', border: '1px solid #374151', borderRadius: 10, color: '#f3f4f6', fontSize: 18, letterSpacing: '0.25em', textAlign: 'center', outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
                          />
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={handle2FADisable}
                            disabled={twoFALoading}
                            className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {twoFALoading ? 'Desactivando...' : <><ShieldOff size={16} />Desactivar 2FA</>}
                          </button>
                          <button
                            onClick={() => { setTwoFAStep('idle'); setTwoFADisableForm({ password: '', code: '' }); }}
                            className="px-4 py-2.5 rounded-lg border border-white/10 text-gray-400 text-sm hover:border-white/20 hover:text-white transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      Alertas Automáticas
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Recibe notificaciones por email y/o webhook cuando ocurran eventos críticos
                    </p>
                  </div>

                  {alertsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw size={24} className="text-accent-purple animate-spin mr-3" />
                      <span className="text-gray-400">Cargando configuración...</span>
                    </div>
                  ) : (
                    <div className="space-y-6">

                      {/* Email + Webhook row */}
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                        {/* Email Card */}
                        <div className="glass-card p-6 border border-white/5">
                          <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
                                <Bell size={18} className="text-blue-400" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-white">Email (SMTP)</h3>
                                <p className="text-xs text-gray-400">Notificaciones por correo</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={alertsConfig.email.enabled}
                                onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, enabled: e.target.checked } }))}
                              />
                              <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-accent-purple peer-checked:to-accent-indigo peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                            </label>
                          </div>

                          {alertsConfig.email.enabled && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Servidor SMTP</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                    placeholder="smtp.gmail.com"
                                    value={alertsConfig.email.smtp_host}
                                    onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, smtp_host: e.target.value } }))}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Puerto</label>
                                  <input
                                    type="number"
                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                    placeholder="587"
                                    value={alertsConfig.email.smtp_port}
                                    onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, smtp_port: parseInt(e.target.value) || 587 } }))}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="smtp-secure"
                                  checked={alertsConfig.email.smtp_secure}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, smtp_secure: e.target.checked } }))}
                                  className="rounded"
                                />
                                <label htmlFor="smtp-secure" className="text-xs text-gray-400 cursor-pointer">Conexión SSL/TLS (puerto 465)</label>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Usuario SMTP</label>
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                  placeholder="usuario@dominio.com"
                                  value={alertsConfig.email.smtp_user}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, smtp_user: e.target.value } }))}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Contraseña SMTP</label>
                                <div className="relative">
                                  <input
                                    type={showSmtpPass ? 'text' : 'password'}
                                    className="w-full px-3 py-2 pr-10 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                    placeholder="••••••••"
                                    value={alertsConfig.email.smtp_pass}
                                    onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, smtp_pass: e.target.value } }))}
                                  />
                                  <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                    onClick={() => setShowSmtpPass(v => !v)}
                                  >
                                    {showSmtpPass ? <EyeOff size={14} /> : <Eye size={14} />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Remitente (From)</label>
                                <input
                                  type="email"
                                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                  placeholder="alertas@midominio.com"
                                  value={alertsConfig.email.from}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, from: e.target.value } }))}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Destinatario (To)</label>
                                <input
                                  type="email"
                                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                  placeholder="admin@midominio.com"
                                  value={alertsConfig.email.to}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, email: { ...prev.email, to: e.target.value } }))}
                                />
                              </div>
                              <button
                                onClick={handleTestEmail}
                                disabled={testingEmail}
                                className="w-full py-2 px-4 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {testingEmail ? <><RefreshCw size={14} className="animate-spin" /> Enviando...</> : <><Bell size={14} /> Enviar email de prueba</>}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Webhook Card */}
                        <div className="glass-card p-6 border border-white/5">
                          <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500/20 to-green-600/20 flex items-center justify-center">
                                <Globe size={18} className="text-green-400" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-white">Webhook HTTP</h3>
                                <p className="text-xs text-gray-400">POST JSON a tu endpoint</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={alertsConfig.webhook.enabled}
                                onChange={e => setAlertsConfig(prev => ({ ...prev, webhook: { ...prev.webhook, enabled: e.target.checked } }))}
                              />
                              <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-accent-purple peer-checked:to-accent-indigo peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                            </label>
                          </div>

                          {alertsConfig.webhook.enabled && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">URL del Webhook</label>
                                <input
                                  type="url"
                                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                  placeholder="https://hooks.slack.com/..."
                                  value={alertsConfig.webhook.url}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, webhook: { ...prev.webhook, url: e.target.value } }))}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Secret (opcional)</label>
                                <input
                                  type="text"
                                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                                  placeholder="Cabecera X-StreamCP-Secret"
                                  value={alertsConfig.webhook.secret}
                                  onChange={e => setAlertsConfig(prev => ({ ...prev, webhook: { ...prev.webhook, secret: e.target.value } }))}
                                />
                              </div>
                              <div className="bg-white/5 rounded-lg p-3 text-xs text-gray-400">
                                <p className="font-medium text-gray-300 mb-1">Payload JSON enviado:</p>
                                <pre className="font-mono text-xs leading-relaxed overflow-x-auto">{`{ "event": "STREAM_OFFLINE",\n  "channel": "mi-canal",\n  "stream_key": "...",\n  "message": "...",\n  "timestamp": "..." }`}</pre>
                              </div>
                              <button
                                onClick={handleTestWebhook}
                                disabled={testingWebhook}
                                className="w-full py-2 px-4 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {testingWebhook ? <><RefreshCw size={14} className="animate-spin" /> Enviando...</> : <><Globe size={14} /> Enviar webhook de prueba</>}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Events to notify */}
                      <div className="glass-card p-6 border border-white/5">
                        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                          <AlertCircle size={16} className="text-accent-purple" />
                          Eventos a notificar
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[
                            { key: 'stream_offline', label: 'Stream desconectado', desc: 'Cuando un canal se desconecta' },
                            { key: 'stream_online',  label: 'Stream en vivo',      desc: 'Cuando un canal entra en vivo' },
                            { key: 'disk_warning',   label: 'Advertencia de disco', desc: 'Uso de disco supera el umbral' },
                            { key: 'disk_critical',  label: 'Disco crítico',        desc: 'Disco casi lleno — acción urgente' },
                            { key: 'ffmpeg_crash',   label: 'FFmpeg crashed',       desc: 'Proceso FFmpeg caído y reiniciado' }
                          ].map(ev => (
                            <label key={ev.key} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                              <input
                                type="checkbox"
                                className="mt-0.5 rounded"
                                checked={alertsConfig.events[ev.key] !== false}
                                onChange={e => setAlertsConfig(prev => ({ ...prev, events: { ...prev.events, [ev.key]: e.target.checked } }))}
                              />
                              <div>
                                <p className="text-sm font-medium text-white">{ev.label}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{ev.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Disk thresholds */}
                      <div className="glass-card p-6 border border-white/5">
                        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                          <Server size={16} className="text-accent-purple" />
                          Umbrales de disco
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Advertencia (%)</label>
                            <input
                              type="number"
                              min="1" max="99"
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                              value={alertsConfig.disk_threshold_warning}
                              onChange={e => setAlertsConfig(prev => ({ ...prev, disk_threshold_warning: parseInt(e.target.value) || 80 }))}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Crítico (%)</label>
                            <input
                              type="number"
                              min="1" max="99"
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                              value={alertsConfig.disk_threshold_critical}
                              onChange={e => setAlertsConfig(prev => ({ ...prev, disk_threshold_critical: parseInt(e.target.value) || 90 }))}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Ruta del disco</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-accent-purple/50"
                              value={alertsConfig.disk_path}
                              onChange={e => setAlertsConfig(prev => ({ ...prev, disk_path: e.target.value }))}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">El disco se revisa cada 5 minutos. Las alertas se limitan a una por cada 10 minutos por evento.</p>
                      </div>

                      {/* Save button */}
                      <div className="flex justify-end">
                        <button
                          onClick={handleSaveAlerts}
                          disabled={alertsLoading}
                          className="px-6 py-2.5 bg-gradient-to-r from-accent-purple to-accent-indigo text-white rounded-lg font-medium hover:shadow-lg hover:shadow-accent-purple/30 transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                          {alertsLoading ? <><RefreshCw size={16} className="animate-spin" /> Guardando...</> : <><Check size={16} /> Guardar configuración</>}
                        </button>
                      </div>

                    </div>
                  )}
                </div>
              )}

              {activeTab === 'ssl' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      Configuración SSL/HTTPS
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Administra la seguridad de tu servidor
                    </p>
                  </div>

                  {/* SSL Status Card */}
                  <div className="glass-card p-6 mb-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 flex items-center justify-center flex-shrink-0">
                        <Lock size={28} className="text-accent-purple" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">Estado SSL</h3>
                        <p className="text-sm text-gray-400">Verifica el estado de tu certificado SSL</p>
                      </div>
                    </div>

                    {sslStatus ? (
                      <div className="space-y-4">
                        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                          sslStatus.enabled
                            ? 'bg-accent-emerald/10 border border-accent-emerald/30'
                            : 'bg-yellow-500/10 border border-yellow-500/30'
                        }`}>
                          {sslStatus.enabled ? (
                            <>
                              <div className="w-10 h-10 rounded-lg bg-accent-emerald/20 flex items-center justify-center">
                                <Check size={20} className="text-accent-emerald" />
                              </div>
                              <div>
                                <span className="text-accent-emerald font-semibold block">SSL Habilitado</span>
                                <span className="text-accent-emerald/80 text-sm">Tu sitio está protegido</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                                <AlertCircle size={20} className="text-yellow-400" />
                              </div>
                              <div>
                                <span className="text-yellow-400 font-semibold block">SSL No Configurado</span>
                                <span className="text-yellow-400/80 text-sm">Se recomienda configurar SSL</span>
                              </div>
                            </>
                          )}
                        </div>
                        {sslStatus.domain && (
                          <div className="flex items-center gap-2 text-sm text-gray-300">
                            <Globe size={16} className="text-accent-indigo" />
                            <span className="font-medium">Dominio:</span>
                            <span className="text-white font-semibold">{sslStatus.domain}</span>
                          </div>
                        )}
                        {sslStatus.expires && (
                          <div className="flex items-center gap-2 text-sm text-gray-300">
                            <Lock size={16} className="text-accent-purple" />
                            <span className="font-medium">Expira:</span>
                            <span className="text-white font-semibold">{sslStatus.expires}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="btn-primary-gradient w-full sm:w-auto"
                        onClick={handleCheckSSL}
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="loading-premium w-4 h-4"></div>
                            Verificando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <RefreshCw size={18} />
                            Verificar Estado SSL
                          </span>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Manual Configuration Card */}
                  <div className="glass-card p-6 mb-6 border border-white/5">
                    <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
                      <Shield size={20} className="text-accent-indigo" />
                      Configuración Manual SSL
                    </h3>
                    <p className="text-sm text-gray-400 mb-6">
                      Para habilitar SSL/HTTPS en tu servidor, sigue estos pasos:
                    </p>
                    <ol className="space-y-4">
                      {[
                        { step: 1, text: 'Asegúrate de tener un dominio apuntando a tu servidor', color: 'accent-purple' },
                        { step: 2, text: 'Instala Certbot:', code: 'apt install certbot python3-certbot-nginx', color: 'accent-indigo' },
                        { step: 3, text: 'Obtén certificado:', code: 'certbot --nginx -d tudominio.com', color: 'accent-emerald' },
                        { step: 4, text: 'Configura renovación automática:', code: 'certbot renew --dry-run', color: 'blue-500' },
                        { step: 5, text: 'Reinicia los servicios de Nginx y backend', color: 'purple-500' }
                      ].map((item) => (
                        <li key={item.step} className="flex gap-4">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-${item.color}/20 to-${item.color}/30 flex items-center justify-center flex-shrink-0`}>
                            <span className={`text-${item.color} font-bold text-sm`}>{item.step}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-300 mb-2">{item.text}</p>
                            {item.code && (
                              <code className="block px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-xs font-mono text-accent-emerald">
                                {item.code}
                              </code>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Warning Card */}
                  <div className="glass-card p-6 border-2 border-red-500/30 bg-red-500/5">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <AlertCircle size={24} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-red-400 font-semibold mb-2 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-red-400 rounded-full animate-pulse"></span>
                          Importante
                        </p>
                        <p className="text-sm text-red-400/90">
                          Después de configurar SSL, debes reiniciar el servicio de backend y actualizar la configuración de Nginx para que los cambios surtan efecto. Asegúrate de realizar un backup antes de aplicar cambios.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'language' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      {t('language')}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Personaliza el idioma y zona horaria del sistema
                    </p>
                  </div>

                  <form onSubmit={handleUpdateLanguage} className="space-y-6">
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Globe size={18} className="text-accent-purple" />
                        Idioma
                      </label>
                      <div className="relative">
                        <select
                          className="input-glass appearance-none cursor-pointer"
                          value={language}
                          onChange={(e) => setLanguage(e.target.value)}
                        >
                          <option value="es" className="bg-dark-lighter text-white">Español</option>
                          <option value="en" className="bg-dark-lighter text-white">English</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-400">
                        Selecciona el idioma de la interfaz
                      </p>
                    </div>

                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <label className="block mb-3 font-semibold text-white flex items-center gap-2">
                        <Clock size={18} className="text-accent-indigo" />
                        Zona Horaria
                      </label>
                      <div className="relative">
                        <select
                          className="input-glass appearance-none cursor-pointer"
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                        >
                          {getAllTimezones().map((tz) => (
                            <option key={tz.value} value={tz.value} className="bg-dark-lighter text-white">
                              {tz.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-400">
                        Las fechas y horas se mostrarán en esta zona horaria
                      </p>
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        className="btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="loading-premium w-4 h-4"></div>
                            Guardando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Check size={18} />
                            {t('savePreferences')}
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'transcoding' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      Transcodificación
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Configura los perfiles de calidad para VOD y streaming en vivo
                    </p>
                  </div>

                  {transcodingLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="loading-premium w-8 h-8"></div>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">

                      {/* VOD Profiles */}
                      <div className="pb-6">
                        <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
                          <Database size={18} className="text-accent-purple" />
                          Perfiles VOD (Video bajo demanda)
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">Calidades disponibles al transcodificar videos subidos</p>
                        <div className="flex flex-wrap gap-3">
                          {['360p', '480p', '720p'].map(p => (
                            <button
                              key={p}
                              onClick={() => toggleProfile('vodProfiles', p)}
                              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                                transcodingConfig.vodProfiles?.includes(p)
                                  ? 'bg-gradient-to-r from-accent-purple to-accent-indigo text-white shadow-lg shadow-accent-purple/30'
                                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-300 mb-2">Preset de codificación VOD</label>
                          <select
                            value={transcodingConfig.vodPreset || 'medium'}
                            onChange={e => setTranscodingConfig(prev => ({ ...prev, vodPreset: e.target.value }))}
                            className="input-glass text-sm"
                          >
                            <option value="ultrafast">ultrafast (más rápido, menor calidad)</option>
                            <option value="superfast">superfast</option>
                            <option value="veryfast">veryfast</option>
                            <option value="faster">faster</option>
                            <option value="fast">fast</option>
                            <option value="medium">medium (recomendado para VOD)</option>
                          </select>
                        </div>
                      </div>

                      {/* Live ABR */}
                      <div className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-base font-semibold text-white flex items-center gap-2">
                              <Activity size={18} className="text-accent-indigo" />
                              ABR en vivo (Adaptive Bitrate)
                            </h3>
                            <p className="text-sm text-gray-400 mt-1">
                              Genera múltiples calidades simultáneas para streams de playlist 24/7
                            </p>
                          </div>
                          <button
                            onClick={() => setTranscodingConfig(prev => ({ ...prev, liveABREnabled: !prev.liveABREnabled }))}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 flex-shrink-0 ml-4 ${
                              transcodingConfig.liveABREnabled ? 'bg-accent-purple' : 'bg-white/20'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                              transcodingConfig.liveABREnabled ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>

                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          transcodingConfig.liveABREnabled ? 'max-h-[700px] opacity-100 mt-5' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}>
                          <div className="space-y-4 pt-4 border-t border-white/10">
                            <div className="px-4 py-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                              <p className="text-xs text-yellow-400">
                                ⚠️ ABR en vivo usa ~3× más CPU. No combinar con override OBS en el mismo canal.
                                Requiere reiniciar la playlist activa para aplicar.
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">Calidades activas</label>
                              <div className="flex flex-wrap gap-3">
                                {['360p', '480p', '720p'].map(p => (
                                  <button
                                    key={p}
                                    onClick={() => toggleProfile('liveProfiles', p)}
                                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                                      transcodingConfig.liveProfiles?.includes(p)
                                        ? 'bg-gradient-to-r from-accent-purple to-accent-indigo text-white shadow-lg shadow-accent-purple/30'
                                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                                    }`}
                                  >
                                    {p}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Preset en vivo</label>
                                <select
                                  value={transcodingConfig.livePreset || 'veryfast'}
                                  onChange={e => setTranscodingConfig(prev => ({ ...prev, livePreset: e.target.value }))}
                                  className="input-glass text-sm"
                                >
                                  <option value="ultrafast">ultrafast (menor calidad, mínimo CPU)</option>
                                  <option value="superfast">superfast</option>
                                  <option value="veryfast">veryfast (recomendado en vivo)</option>
                                  <option value="faster">faster</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                  Bitrate máximo 720p (kbps)
                                </label>
                                <input
                                  type="number"
                                  min="500"
                                  max="8000"
                                  step="100"
                                  value={transcodingConfig.liveMaxBitrate || 2500}
                                  onChange={e => setTranscodingConfig(prev => ({ ...prev, liveMaxBitrate: parseInt(e.target.value) || 2500 }))}
                                  className="input-glass text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-6">
                        <button
                          onClick={handleSaveTranscoding}
                          disabled={transcodingLoading}
                          className="btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {transcodingLoading ? (
                            <span className="flex items-center gap-2">
                              <div className="loading-premium w-4 h-4"></div>
                              Guardando...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Check size={18} />
                              Guardar configuración
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'branding' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      Personalización del Reproductor
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Añade tu logo y un texto en scroll al reproductor de video
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* ── Watermark Card ── */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 flex items-center justify-center border border-accent-purple/30">
                            <Image size={20} className="text-accent-purple" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white text-base">Logo / Watermark</h3>
                            <p className="text-xs text-gray-400">Superpone tu logo sobre el video</p>
                          </div>
                        </div>
                        {/* Toggle */}
                        <button
                          onClick={() => setBranding(prev => ({
                            ...prev,
                            watermark: { ...prev.watermark, enabled: !prev.watermark.enabled }
                          }))}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            branding.watermark.enabled ? 'bg-accent-purple' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                            branding.watermark.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className={`space-y-4 ${!branding.watermark.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        {/* Preview */}
                        {branding.watermark.url && (
                          <div className="flex items-center gap-4 p-3 rounded-xl bg-black/30 border border-white/10">
                            <img
                              src={branding.watermark.url}
                              alt="watermark preview"
                              className="max-h-14 max-w-[160px] object-contain"
                              style={{ opacity: branding.watermark.opacity }}
                            />
                            <div className="text-xs text-gray-400">Vista previa del logo</div>
                          </div>
                        )}

                        {/* Upload button */}
                        <div>
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg,.svg,.webp"
                            ref={watermarkFileRef}
                            onChange={handleWatermarkUpload}
                            className="hidden"
                          />
                          <button
                            onClick={() => watermarkFileRef.current?.click()}
                            disabled={brandingLoading}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50"
                          >
                            <Upload size={16} />
                            {branding.watermark.url ? 'Cambiar Logo (PNG/SVG)' : 'Subir Logo (PNG/SVG)'}
                          </button>
                        </div>

                        {/* Position */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Posición</label>
                          <select
                            value={branding.watermark.position}
                            onChange={e => setBranding(prev => ({
                              ...prev,
                              watermark: { ...prev.watermark, position: e.target.value }
                            }))}
                            className="input-glass w-full px-4 py-2.5 rounded-xl text-sm"
                          >
                            <option value="top-left">Arriba a la izquierda</option>
                            <option value="top-right">Arriba a la derecha</option>
                            <option value="bottom-left">Abajo a la izquierda</option>
                            <option value="bottom-right">Abajo a la derecha</option>
                          </select>
                        </div>

                        {/* Opacity */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Opacidad: <span className="text-accent-purple font-bold">{Math.round(branding.watermark.opacity * 100)}%</span>
                          </label>
                          <input
                            type="range"
                            min="0.1"
                            max="1"
                            step="0.05"
                            value={branding.watermark.opacity}
                            onChange={e => setBranding(prev => ({
                              ...prev,
                              watermark: { ...prev.watermark, opacity: parseFloat(e.target.value) }
                            }))}
                            className="w-full accent-accent-purple"
                          />
                        </div>

                        {/* Scale */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Escala: <span className="text-accent-purple font-bold">{branding.watermark.size ?? 100}%</span>
                          </label>
                          <input
                            type="range"
                            min="20"
                            max="300"
                            step="5"
                            value={branding.watermark.size ?? 100}
                            onChange={e => setBranding(prev => ({
                              ...prev,
                              watermark: { ...prev.watermark, size: parseInt(e.target.value) }
                            }))}
                            className="w-full accent-accent-purple"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>20%</span><span>100%</span><span>300%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Ticker Card ── */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-indigo/30 transition-all duration-300">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-indigo/20 to-blue-500/20 flex items-center justify-center border border-accent-indigo/30">
                            <AlignLeft size={20} className="text-accent-indigo" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white text-base">Texto en Scroll (Ticker)</h3>
                            <p className="text-xs text-gray-400">Mensaje desplazable en la parte inferior del video</p>
                          </div>
                        </div>
                        {/* Toggle */}
                        <button
                          onClick={() => setBranding(prev => ({
                            ...prev,
                            ticker: { ...prev.ticker, enabled: !prev.ticker.enabled }
                          }))}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            branding.ticker.enabled ? 'bg-accent-indigo' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                            branding.ticker.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className={`space-y-4 ${!branding.ticker.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Mensaje del Ticker</label>
                          <input
                            type="text"
                            value={branding.ticker.text}
                            onChange={e => setBranding(prev => ({
                              ...prev,
                              ticker: { ...prev.ticker, text: e.target.value }
                            }))}
                            placeholder="Ej: Bienvenidos a nuestro canal en vivo • Síguenos en redes sociales"
                            className="input-glass w-full px-4 py-3 rounded-xl text-sm"
                          />
                        </div>

                        {/* Font size + weight row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Font size */}
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Tamaño de fuente: <span className="text-accent-indigo font-bold">{branding.ticker.fontSize ?? 14}px</span>
                            </label>
                            <input
                              type="range"
                              min="10"
                              max="32"
                              step="1"
                              value={branding.ticker.fontSize ?? 14}
                              onChange={e => setBranding(prev => ({
                                ...prev,
                                ticker: { ...prev.ticker, fontSize: parseInt(e.target.value) }
                              }))}
                              className="w-full accent-accent-indigo"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>10px</span><span>32px</span>
                            </div>
                          </div>

                          {/* Font weight */}
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Grosor de fuente</label>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[
                                { label: 'Fina', value: 300 },
                                { label: 'Normal', value: 400 },
                                { label: 'Semibold', value: 600 },
                                { label: 'Negrita', value: 700 },
                                { label: 'Extra', value: 800 },
                                { label: '900', value: 900 }
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => setBranding(prev => ({
                                    ...prev,
                                    ticker: { ...prev.ticker, fontWeight: opt.value }
                                  }))}
                                  className={`px-2 py-1.5 rounded-lg text-xs transition-all ${
                                    (branding.ticker.fontWeight ?? 500) === opt.value
                                      ? 'bg-accent-indigo text-white font-semibold'
                                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                  }`}
                                  style={{ fontWeight: opt.value }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Live preview */}
                        {branding.ticker.text && (
                          <div className="rounded-xl bg-black overflow-hidden relative border border-white/10"
                               style={{ height: Math.max(36, (branding.ticker.fontSize ?? 14) + 22) + 'px' }}>
                            <div className="flex h-full items-center">
                              <div style={{
                                display: 'flex',
                                animation: 'ticker-scroll 12s linear infinite',
                                whiteSpace: 'nowrap'
                              }}>
                                <span style={{ padding: '0 40px', color: 'white', fontSize: `${branding.ticker.fontSize ?? 14}px`, fontWeight: branding.ticker.fontWeight ?? 500, lineHeight: 1.2 }}>
                                  {branding.ticker.text}
                                </span>
                                <span style={{ padding: '0 40px', color: 'white', fontSize: `${branding.ticker.fontSize ?? 14}px`, fontWeight: branding.ticker.fontWeight ?? 500, lineHeight: 1.2 }}>
                                  {branding.ticker.text}
                                </span>
                              </div>
                            </div>
                            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to right, #000 0%, transparent 15%, transparent 85%, #000 100%)' }} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Transitions Card ── */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-purple/20 to-blue-500/20 flex items-center justify-center border border-accent-purple/30">
                            <Layers size={20} className="text-accent-purple" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white text-base">Transiciones Suaves</h3>
                            <p className="text-xs text-gray-400">Fade-out/in al cambiar de bloque horario</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setBranding(prev => ({
                            ...prev,
                            transitions: { ...prev.transitions, enabled: !prev.transitions.enabled }
                          }))}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            branding.transitions.enabled ? 'bg-accent-purple' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                            branding.transitions.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className={`${!branding.transitions.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Duración del fade:{' '}
                          <span className="text-accent-purple font-bold">{branding.transitions.duration ?? 2}s</span>
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="5"
                          step="0.5"
                          value={branding.transitions.duration ?? 2}
                          onChange={e => setBranding(prev => ({
                            ...prev,
                            transitions: { ...prev.transitions, duration: parseFloat(e.target.value) }
                          }))}
                          className="w-full accent-accent-purple"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>0.5s</span><span>2s</span><span>5s</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          El video saliente se desvanece en negro y el entrante aparece suavemente. También se aplica al audio.
                        </p>
                      </div>
                    </div>

                    {/* ── Sloop (Emergency Loop) Card ── */}
                    <div className="glass-card p-6 border border-white/5 hover:border-red-500/30 transition-all duration-300">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-600/20 to-orange-500/20 flex items-center justify-center border border-red-600/30">
                            <Film size={20} className="text-red-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white text-base">Video de Emergencia (Sloop)</h3>
                            <p className="text-xs text-gray-400">Se reproduce en bucle cuando no hay contenido programado</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setBranding(prev => ({
                            ...prev,
                            sloop: { ...prev.sloop, enabled: !prev.sloop.enabled }
                          }))}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                            branding.sloop.enabled ? 'bg-red-500' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow ${
                            branding.sloop.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className={`space-y-4 ${!branding.sloop.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                        {/* Current sloop file */}
                        {branding.sloop.url && (
                          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-black/30 border border-white/10">
                            <Film size={16} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-300 truncate">
                              {branding.sloop.videoName || branding.sloop.url.split('/').pop()}
                            </span>
                            <span className="ml-auto text-xs text-green-400 flex-shrink-0">✓ Cargado</span>
                          </div>
                        )}

                        {/* Upload button */}
                        <div>
                          <input
                            type="file"
                            accept=".mp4,.mov,.avi,.mkv,.webm"
                            ref={sloopFileRef}
                            onChange={handleSloopUpload}
                            className="hidden"
                          />
                          <button
                            onClick={() => sloopFileRef.current?.click()}
                            disabled={brandingLoading}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50"
                          >
                            <Upload size={16} />
                            {branding.sloop.url ? 'Cambiar video de emergencia' : 'Subir video de emergencia (MP4)'}
                          </button>
                        </div>

                        <div className="px-3 py-2.5 rounded-xl bg-orange-500/5 border border-orange-500/20">
                          <p className="text-xs text-orange-400">
                            ⚠️ El video se reproducirá en bucle automáticamente cuando el reproductor detecte que no hay stream disponible (sin contenido programado, error de conexión, etc.). Se recuperará solo al restablecerse la señal.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Save button */}
                    <button
                      onClick={handleSaveBranding}
                      disabled={brandingLoading}
                      className="btn-primary-gradient w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {brandingLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="loading-premium w-4 h-4"></div>
                          Guardando...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Check size={18} />
                          Guardar Configuración del Reproductor
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'maintenance' && (
                <div className="animate-fadeIn space-y-6">
                  <div className="mb-2">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-amber-400 bg-clip-text text-transparent">
                      Modo Mantenimiento
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Cuando está activo, los reproductores públicos muestran una pantalla de mantenimiento en lugar del stream.
                    </p>
                  </div>

                  {/* Toggle card */}
                  <div className={`glass-card p-6 border transition-all duration-300 ${maintenance.enabled ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/5'}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${maintenance.enabled ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-white/5 border border-white/10'}`}>
                          <Wrench size={22} className={maintenance.enabled ? 'text-amber-400' : 'text-gray-400'} />
                        </div>
                        <div>
                          <p className="text-base font-semibold text-white">Modo Mantenimiento</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {maintenance.enabled
                              ? '🟠 Activo — los espectadores ven la pantalla de mantenimiento'
                              : 'Inactivo — el player funciona con normalidad'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setMaintenance(prev => ({ ...prev, enabled: !prev.enabled }))}
                        className="flex-shrink-0"
                      >
                        {maintenance.enabled
                          ? <ToggleRight size={40} className="text-amber-400" />
                          : <ToggleLeft size={40} className="text-gray-500" />}
                      </button>
                    </div>
                  </div>

                  {/* Message textarea */}
                  <div className="glass-card p-6 border border-white/5">
                    <label className="block text-sm font-semibold text-gray-300 mb-3">
                      Mensaje para los espectadores
                    </label>
                    <textarea
                      rows={4}
                      maxLength={500}
                      className="input-glass resize-none w-full"
                      value={maintenance.message}
                      onChange={(e) => setMaintenance(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Estamos realizando tareas de mantenimiento. Volvemos pronto."
                    />
                    <p className="text-xs text-gray-600 mt-2 text-right">{maintenance.message.length}/500</p>
                  </div>

                  {/* Preview */}
                  <div className="glass-card p-6 border border-white/5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Vista previa del mensaje</p>
                    <div className="rounded-xl bg-black/60 border border-white/10 p-8 flex flex-col items-center justify-center gap-4 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                        <Wrench size={32} className="text-amber-400" />
                      </div>
                      <p className="text-white font-semibold text-lg">En Mantenimiento</p>
                      <p className="text-gray-400 text-sm max-w-xs leading-relaxed">
                        {maintenance.message || 'Estamos realizando tareas de mantenimiento. Volvemos pronto.'}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={saveMaintenance}
                    disabled={maintenanceLoading}
                    className="btn-primary-gradient w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {maintenanceLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="loading-premium w-4 h-4"></div>
                        Guardando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Check size={18} />
                        Guardar configuración
                      </span>
                    )}
                  </button>
                </div>
              )}

              {activeTab === 'system' && (
                <div className="animate-fadeIn">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                      Resetear Sistema
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Reinicia servicios del sistema para resolver problemas
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Nginx Card */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-purple/30 transition-all duration-300 group">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-accent-purple/30 transition-all duration-300">
                          <Server size={28} className="text-accent-purple" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">Nginx Server</h3>
                          <p className="text-sm text-gray-400">Reinicia el servidor web y proxy RTMP</p>
                        </div>
                      </div>
                      {restartConfirm === 'nginx' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRestartService('nginx')}
                            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200"
                          >
                            ¿Confirmar reinicio?
                          </button>
                          <button
                            onClick={() => setRestartConfirm(null)}
                            className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleRestartService('nginx')}
                          disabled={restartLoading === 'nginx'}
                          className="btn-primary-gradient w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {restartLoading === 'nginx' ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="loading-premium w-4 h-4"></div>
                              Reiniciando...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <RotateCw size={18} />
                              Reiniciar Nginx
                            </span>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Backend Card */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-indigo/30 transition-all duration-300 group">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-indigo/20 to-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-accent-indigo/30 transition-all duration-300">
                          <Activity size={28} className="text-accent-indigo" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">Backend Server</h3>
                          <p className="text-sm text-gray-400">Reinicia el servidor de la aplicación</p>
                        </div>
                      </div>
                      {restartConfirm === 'backend' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRestartService('backend')}
                            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200"
                          >
                            ¿Confirmar reinicio?
                          </button>
                          <button
                            onClick={() => setRestartConfirm(null)}
                            className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleRestartService('backend')}
                          disabled={restartLoading === 'backend'}
                          className="bg-gradient-to-r from-accent-indigo to-blue-500 hover:from-accent-indigo/90 hover:to-blue-500/90 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-accent-indigo/50 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        >
                          {restartLoading === 'backend' ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="loading-premium w-4 h-4"></div>
                              Reiniciando...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <RotateCw size={18} />
                              Reiniciar Backend
                            </span>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Database Card */}
                    <div className="glass-card p-6 border border-white/5 hover:border-accent-emerald/30 transition-all duration-300 group">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-emerald/20 to-green-500/20 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-accent-emerald/30 transition-all duration-300">
                          <Database size={28} className="text-accent-emerald" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">Base de Datos</h3>
                          <p className="text-sm text-gray-400">Optimiza y compacta la base de datos SQLite</p>
                        </div>
                      </div>
                      {restartConfirm === 'database' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRestartService('database')}
                            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200"
                          >
                            ¿Confirmar optimización?
                          </button>
                          <button
                            onClick={() => setRestartConfirm(null)}
                            className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleRestartService('database')}
                          disabled={restartLoading === 'database'}
                          className="bg-gradient-to-r from-accent-emerald to-green-500 hover:from-accent-emerald/90 hover:to-green-500/90 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-accent-emerald/50 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        >
                          {restartLoading === 'database' ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="loading-premium w-4 h-4"></div>
                              Optimizando...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <Database size={18} />
                              Optimizar Database
                            </span>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Warning Card */}
                    <div className="glass-card p-6 border-2 border-yellow-500/30 bg-yellow-500/5">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                          <AlertCircle size={24} className="text-yellow-400" />
                        </div>
                        <div>
                          <p className="text-sm text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                            Advertencia
                          </p>
                          <p className="text-sm text-yellow-400/90">
                            Reiniciar servicios puede causar una interrupción temporal en streams activos. Se recomienda realizar estas acciones cuando no haya transmisiones en vivo.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── USERS TAB ─────────────────────────────────────────────── */}
              {activeTab === 'users' && (
                <div className="space-y-6">

                  {/* Header row */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-400">
                      {usersData.length} usuario{usersData.length !== 1 ? 's' : ''} registrado{usersData.length !== 1 ? 's' : ''}
                    </p>
                    <button
                      className="btn-primary-gradient flex items-center gap-2"
                      onClick={() => setShowCreateUserModal(true)}>
                      <UserPlus size={16} /> Nuevo Usuario
                    </button>
                  </div>

                  {/* Users list */}
                  {usersLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="loading-premium w-8 h-8" />
                    </div>
                  ) : usersData.length === 0 ? (
                    <div className="glass-card p-12 text-center">
                      <Users size={40} className="mx-auto text-gray-600 mb-3" />
                      <p className="text-gray-400">No hay usuarios registrados</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {usersData.map(u => {
                        const roleMeta = ROLE_META[u.role] || ROLE_META.user;
                        const RoleIcon = roleMeta.icon;
                        const initials = u.full_name
                          ? u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                          : u.email.slice(0, 2).toUpperCase();
                        const isSelf = u.id === user?.id;

                        return (
                          <div key={u.id}
                            className={`glass-card p-4 flex items-center gap-4 transition-all duration-200 ${
                              !u.is_active ? 'opacity-60' : 'hover:border-white/20'
                            }`}>

                            {/* Avatar */}
                            <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-white font-bold text-sm
                              ${u.role === 'admin' ? 'bg-gradient-to-br from-red-500 to-rose-600 shadow-md shadow-red-500/30'
                                : u.role === 'operator' ? 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-500/30'
                                : 'bg-gradient-to-br from-gray-600 to-gray-700'}`}>
                              {initials}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-white text-sm truncate">
                                  {u.full_name || u.email.split('@')[0]}
                                </span>
                                {isSelf && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent-purple/20 text-accent-purple border border-accent-purple/30">
                                    Tú
                                  </span>
                                )}
                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${roleMeta.color}`}>
                                  <RoleIcon size={10} /> {roleMeta.label}
                                </span>
                                {!u.is_active && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400 border border-gray-600">
                                    Inactivo
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 truncate mt-0.5">{u.email}</div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {/* Edit */}
                              <button
                                title="Editar"
                                onClick={() => { setEditingUser({ ...u }); setShowEditUserModal(true); }}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-center transition-all text-gray-400 hover:text-white">
                                <Edit3 size={14} />
                              </button>

                              {/* Reset password */}
                              <button
                                title="Restablecer contraseña"
                                onClick={() => { setEditingUser(u); setShowResetPassModal(true); }}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/30 flex items-center justify-center transition-all text-gray-400 hover:text-amber-400">
                                <KeyRound size={14} />
                              </button>

                              {/* Toggle active */}
                              {!isSelf && (
                                <button
                                  title={u.is_active ? 'Desactivar' : 'Activar'}
                                  onClick={() => handleToggleUserStatus(u.id, u.is_active)}
                                  className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                                    u.is_active
                                      ? 'bg-white/5 hover:bg-yellow-500/20 border-white/10 hover:border-yellow-500/30 text-gray-400 hover:text-yellow-400'
                                      : 'bg-green-500/10 hover:bg-green-500/20 border-green-500/20 hover:border-green-500/40 text-green-400'
                                  }`}>
                                  {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                                </button>
                              )}

                              {/* Delete */}
                              {!isSelf && (
                                deletingUserId === u.id ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleDeleteUser(u.id)}
                                      className="px-2 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 text-red-400 text-xs font-semibold transition-all">
                                      Sí
                                    </button>
                                    <button
                                      onClick={() => setDeletingUserId(null)}
                                      className="px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 text-xs transition-all">
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    title="Eliminar"
                                    onClick={() => setDeletingUserId(u.id)}
                                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 flex items-center justify-center transition-all text-gray-400 hover:text-red-400">
                                    <Trash2 size={14} />
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Role legend */}
                  <div className="glass-card p-4 border border-white/5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Roles disponibles</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 flex-shrink-0 mt-0.5">
                          <Crown size={10} /> Admin
                        </span>
                        <p className="text-xs text-gray-500">Acceso total: usuarios, configuración, streams y contenido</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 flex-shrink-0 mt-0.5">
                          <UserCheck size={10} /> Operador
                        </span>
                        <p className="text-xs text-gray-500">Gestión de canales, VODs, playlists y spots. Sin acceso a usuarios ni sistema</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30 flex-shrink-0 mt-0.5">
                          <Eye size={10} /> Visor
                        </span>
                        <p className="text-xs text-gray-500">Solo lectura: dashboard y analíticas</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── CREATE USER MODAL ────────────────────────────────────────────── */}
      {showCreateUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateUserModal(false)}>
          <div className="glass-card p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <UserPlus size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Nuevo Usuario</h3>
                <p className="text-xs text-gray-400">Crea una cuenta con acceso al panel</p>
              </div>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={newUserForm.full_name}
                  onChange={e => setNewUserForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Ej: Juan García"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors placeholder:text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
                <input
                  type="email"
                  required
                  value={newUserForm.email}
                  onChange={e => setNewUserForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="usuario@empresa.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors placeholder:text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Contraseña *</label>
                <div className="relative">
                  <input
                    type={showNewUserPass ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={newUserForm.password}
                    onChange={e => setNewUserForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full px-4 py-2.5 pr-10 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors placeholder:text-gray-600"
                  />
                  <button type="button" onClick={() => setShowNewUserPass(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                    {showNewUserPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Rol</label>
                <select
                  value={newUserForm.role}
                  onChange={e => setNewUserForm(p => ({ ...p, role: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors appearance-none">
                  <option value="operator" className="bg-gray-900">Operador</option>
                  <option value="viewer" className="bg-gray-900">Visor</option>
                  <option value="admin" className="bg-gray-900">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={usersLoading} className="btn-primary-gradient flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  {usersLoading ? <div className="loading-premium w-4 h-4" /> : <><UserPlus size={16} /> Crear Usuario</>}
                </button>
                <button type="button" onClick={() => setShowCreateUserModal(false)} className="btn-glass px-4">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT USER MODAL ──────────────────────────────────────────────── */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowEditUserModal(false)}>
          <div className="glass-card p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Edit3 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Editar Usuario</h3>
                <p className="text-xs text-gray-400">{editingUser.email}</p>
              </div>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={editingUser.full_name || ''}
                  onChange={e => setEditingUser(p => ({ ...p, full_name: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={editingUser.email}
                  onChange={e => setEditingUser(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Rol</label>
                <select
                  value={editingUser.role}
                  onChange={e => setEditingUser(p => ({ ...p, role: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-accent-purple/50 focus:outline-none text-white text-sm transition-colors appearance-none">
                  <option value="operator" className="bg-gray-900">Operador</option>
                  <option value="viewer" className="bg-gray-900">Visor</option>
                  <option value="admin" className="bg-gray-900">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={usersLoading} className="btn-primary-gradient flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  {usersLoading ? <div className="loading-premium w-4 h-4" /> : <><Check size={16} /> Guardar Cambios</>}
                </button>
                <button type="button" onClick={() => setShowEditUserModal(false)} className="btn-glass px-4">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── RESET PASSWORD MODAL ─────────────────────────────────────────── */}
      {showResetPassModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowResetPassModal(false)}>
          <div className="glass-card p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <KeyRound size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Restablecer Contraseña</h3>
                <p className="text-xs text-gray-400">{editingUser.full_name || editingUser.email}</p>
              </div>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Nueva contraseña *</label>
                <div className="relative">
                  <input
                    type={showResetPassField ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={resetPassForm.new_password}
                    onChange={e => setResetPassForm(p => ({ ...p, new_password: e.target.value }))}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full px-4 py-2.5 pr-10 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none text-white text-sm transition-colors placeholder:text-gray-600"
                  />
                  <button type="button" onClick={() => setShowResetPassField(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                    {showResetPassField ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirmar contraseña *</label>
                <input
                  type={showResetPassField ? 'text' : 'password'}
                  required
                  value={resetPassForm.confirm}
                  onChange={e => setResetPassForm(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Repite la contraseña"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none text-white text-sm transition-colors placeholder:text-gray-600"
                />
              </div>
              {resetPassForm.confirm && resetPassForm.new_password !== resetPassForm.confirm && (
                <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle size={12} /> Las contraseñas no coinciden</p>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={usersLoading || (resetPassForm.confirm && resetPassForm.new_password !== resetPassForm.confirm)}
                  className="btn-primary-gradient flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  {usersLoading ? <div className="loading-premium w-4 h-4" /> : <><KeyRound size={16} /> Restablecer</>}
                </button>
                <button type="button" onClick={() => { setShowResetPassModal(false); setResetPassForm({ new_password: '', confirm: '' }); }} className="btn-glass px-4">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Settings;

